import { Component, computed, inject, Input, OnDestroy, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { RouterLink } from '@angular/router';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { DialogModule } from 'primeng/dialog';
import { FloatLabelModule } from 'primeng/floatlabel';
import { ProviderService } from '../services/providers.service';
import {
  CoesProviderStatus,
  Provider,
  ProviderFormData,
  ProviderStatus,
  SelectOption,
} from '../interfaces/providers-interface';
import { MenuItem, MessageService } from 'primeng/api';
import { forkJoin, of, Subject } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { BreadcrumbNavigationService } from '@angular-monorepo/ui';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { PasswordModule } from 'primeng/password';
import { SelectModule } from 'primeng/select';
import { animate, style, transition, trigger } from '@angular/animations';
import { ProvidersTableComponent } from '../components/tables/providers-table.component';
import { Panel } from 'primeng/panel';
import { RoleOption, UsersService } from '@angular-monorepo/users';

@Component({
  selector: 'lib-providers',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    InputTextModule,
    ToastModule,
    TooltipModule,
    FormsModule,
    ReactiveFormsModule,
    RouterLink,
    BreadcrumbModule,
    DialogModule,
    FloatLabelModule,
    PasswordModule,
    TranslatePipe,
    ProvidersTableComponent,
    SelectModule,
    Panel,
  ],
  providers: [MessageService],
  templateUrl: './providers.component.html',
  styleUrl: './providers.component.css',
  animations: [
    trigger('fadeInOut', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(-10px)' }),
        animate('300ms ease-out', style({ opacity: 1, transform: 'translateY(0)' })),
      ]),
      transition(':leave', [
        animate('200ms ease-in', style({ opacity: 0, transform: 'translateY(-10px)' })),
      ]),
    ]),
    trigger('smoothResize', [
      transition(
        '* <=> *',
        [
          style({ height: '{{startHeight}}px' }),
          animate('300ms ease-in-out', style({ height: '*' })),
        ],
        { params: { startHeight: 0 } },
      ),
    ]),
  ],
})
export class ProvidersComponent implements OnInit, OnDestroy {
  createProviderForm!: FormGroup;
  editProviderForm!: FormGroup;
  currentEdit: Provider | null = null;
  providerToDelete: Provider | null = null;
  showDeleteConfirmDialog = false;
  showEditDialog = false;
  editableSection = signal<'general' | null>(null);

  // Select Searches
  countriesOptions: SelectOption[] = [];
  provincesOptions: SelectOption[] = [];
  communesOptions: SelectOption[] = [];

  countrySearch$ = new Subject<string>();
  provinceSearch$ = new Subject<string>();
  communeSearch$ = new Subject<string>();

  loadingRoles = false;
  roles: RoleOption[] = [];

  selectedStatus: ProviderStatus = 'all';
  selectedCoesStatus: CoesProviderStatus = '';
  rows = 10;
  first = 0;
  totalRecords = 0;
  accountTypeOptions: SelectOption[] = [];
  currencyOptions: SelectOption[] = [];
  statusOptions: { label: string; value: ProviderStatus; icon: string }[] = [
    { label: 'Estado', value: 'all', icon: 'pi pi-list' },
    { label: 'Activos', value: 'active', icon: 'pi pi-check-circle' },
    { label: 'Inactivos', value: 'inactive', icon: 'pi pi-times-circle' },
    { label: 'Inactivos sin usuarios', value: 'pending', icon: 'pi pi-clock' },
  ];
  coesStatusOptions: { label: string; value: CoesProviderStatus; icon: string }[] = [
    { label: 'Todos', value: '', icon: 'pi pi-list' },
    { label: 'COES', value: 'coes', icon: 'pi pi-check-circle' },
    { label: 'No COES', value: 'no coes', icon: 'pi pi-times-circle' },
  ];
  isPeru = false;
  searchTerm = '';
  @Input() breadcrumbs = signal<MenuItem[]>([]);
  loading = false;
  canEditPeruFields = computed(() => {
    console.log('Aqui', this.isPeru, this.editableSection() === 'general');
    return this.isPeru && this.editableSection() === 'general';
  });
  private readonly providerService = inject(ProviderService);
  providers$ = this.providerService.providers$;
  loading$ = this.providerService.loading$;
  private readonly messageService = inject(MessageService);
  private readonly translate = inject(TranslateService);
  private readonly userService = inject(UsersService);
  private readonly fb = inject(FormBuilder);
  private readonly breadcrumbNav = inject(BreadcrumbNavigationService);
  public displayBreadcrumbs = computed(() => this.breadcrumbNav.generateBreadcrumbs('providers'));
  private readonly status$ = new Subject<ProviderStatus>();
  private readonly coesStatus$ = new Subject<CoesProviderStatus>();
  private readonly search$ = new Subject<string>();
  private readonly destroy$ = new Subject<void>();
  private readonly autoDeactivatingProviderIds = new Set<number>();

  ngOnInit() {
    this.initForms();
    this.setupCountrySearch();
    this.setupProvinceSearch();
    this.setupCommuneSearch();
    this.setupSearchDebounce();
    this.setupStatusDebounce();
    this.setupCoesStatusDebounce();
    this.buildSelectOptions();
    this.loadRolesProvider();
    this.loading$?.subscribe((value) => (this.loading = value));

    this.providerService.getCountries('').subscribe((res) => {
      this.countriesOptions = res.items.map((item) => ({
        label: item.name,
        value: String(item.country),
      }));
    });

    this.providerService.getProvinces('').subscribe((res) => {
      this.provincesOptions = res.items.map((item) => ({
        label: item.name,
        value: String(item.city),
      }));
    });

    this.providerService.getCommunes('').subscribe((res) => {
      this.communesOptions = res.items.map((item) => ({
        label: item.name,
        value: String(item.comune),
      }));
    });

    this.loadProviders(1, '');

    this.translate.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.buildSelectOptions();
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggleGeneralSection() {
    if (this.isProviderUpdateBlocked()) {
      return;
    }

    if (this.editableSection() === 'general') {
      this.editableSection.set(null);
      this.editProviderForm.disable();
    } else {
      this.editableSection.set('general');
      this.editProviderForm.enable();
    }
  }

  showEditModal(provider: Provider) {
    this.currentEdit = { ...provider };

    this.editableSection.set(null);
    this.editProviderForm.disable();

    const normalized = Array.from((provider.country ?? '').normalize('NFD'))
      .filter((ch) => {
        const cp = ch.codePointAt(0) ?? 0;
        return cp < 0x300 || cp > 0x036f;
      })
      .join('')
      .trim()
      .toLowerCase();

    this.isPeru = normalized === 'peru';

    let countryValue = null;
    if (provider.country) {
      const ctry = this.countriesOptions.find(
        (c) => c.label.toLowerCase() === provider.country?.toLowerCase(),
      );
      countryValue = ctry ? ctry.value : null;
    }

    let provinceValue = null;
    if (provider.city) {
      const prov = this.provincesOptions.find(
        (p) => p.label.toLowerCase() === provider.city?.toLowerCase(),
      );
      provinceValue = prov ? prov.value : null;
    }

    let comuneValue = null;
    if (provider.comune) {
      const com = this.communesOptions.find(
        (c) => c.label.toLowerCase() === provider.comune?.toLowerCase(),
      );
      comuneValue = com ? com.value : null;
    }

    this.editProviderForm.patchValue({
      country: this.isPeru && countryValue ? countryValue : null,
      city: this.isPeru && provinceValue ? provinceValue : null,
      comune: this.isPeru && comuneValue ? comuneValue : null,
      provider_name: provider.provider_name,
      business_name: provider.business_name,
      RUC: provider.ruc,
      email: provider.email,
      phone: provider.phone,
      role: provider.user_data?.role ?? null,
      address: provider.address,
    });

    this.showEditDialog = true;
  }

  hideEditModal() {
    this.showEditDialog = false;
    this.currentEdit = null;
    this.editProviderForm.reset();
  }

  submitEditForm() {
    if (this.isProviderUpdateBlocked()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Datos incompletos',
        detail: this.getProviderUpdateBlockedMessage(),
      });
      return;
    }

    if (this.editProviderForm.invalid) {
      this.editProviderForm.markAllAsTouched();
      return;
    }

    if (!this.currentEdit) {
      return;
    }

    const original = this.currentEdit;

    const isCoes = this.currentEdit.coes === 'COES';

    const formData = this.prepareFormData(this.editProviderForm.value, original);

    if (isCoes) {
      delete formData.user.password;
    }

    this.providerService.updateProvider(this.currentEdit.id, formData).subscribe({
      next: (updatedProvider) => {
        this.messageService.add({
          severity: 'success',
          summary: 'Proveedor actualizado',
          detail: `${updatedProvider.provider_name} fue actualizado exitosamente`,
        });
        this.hideEditModal();
        this.loadProviders(Math.floor(this.first / this.rows) + 1, this.searchTerm);
      },
      error: (error) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: error.message || 'No se pudo actualizar el proveedor',
        });
      },
    });
  }

  getStatusDescription(): string {
    switch (this.selectedStatus) {
      case 'inactive':
        return 'Muestra los proveedores desactivados.';
      case 'pending':
        return 'Muestra los proveedores que no tienen credenciales aún (no tienen contraseña asignada).';
      case 'active':
        return 'Muestra los proveedores activos.';
      default:
        return 'Muestra todos los proveedores sin filtrar por estado.';
    }
  }

  getCoesDescription(): string {
    switch (this.selectedCoesStatus) {
      case 'coes':
        return 'Incluye solo los proveedores que son COES.';
      case 'no coes':
        return 'Incluye solo los proveedores que no son COES.';
      default:
        return 'Incluye todos los proveedores independientemente de COES.';
    }
  }

  onStatusFilterChange(status: ProviderStatus) {
    this.status$.next(status);
  }

  onCoesStatusFilterChange(status: CoesProviderStatus) {
    this.coesStatus$.next(status);
  }

  onSearchInput() {
    this.search$.next(this.searchTerm);
  }

  onSearchClear() {
    this.searchTerm = '';
    this.search$.next('');
  }

  hasActiveFilters(): boolean {
    return (
      Boolean(this.searchTerm?.trim()) || this.selectedStatus !== 'all' || !!this.selectedCoesStatus
    );
  }

  clearFilters() {
    this.searchTerm = '';
    this.selectedStatus = 'all';
    this.selectedCoesStatus = '';
    this.search$.next('');
    this.status$.next('all');
    this.coesStatus$.next('');
  }

  onEdit(provider: Provider) {
    if (!provider) {
      return;
    }

    this.providerService.getProviderById(String(provider.id)).subscribe((full) => {
      this.showEditModal(full);
    });
  }

  confirmDelete(provider: Provider) {
    this.providerToDelete = provider;
    this.showDeleteConfirmDialog = true;
  }

  onToggleStatus(event: { provider: Provider; status: boolean }) {
    const { provider, status } = event;

    if (status && !this.hasRequiredProviderFields(provider)) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Datos incompletos',
        detail: 'No se puede activar el proveedor porque faltan campos requeridos.',
      });
      return;
    }

    const payload: Partial<Provider> = { is_active: status } as Partial<Provider>;
    this.providerService.updateProvider(provider.id, payload).subscribe({
      next: () => {
        this.messageService.add({
          severity: status ? 'success' : 'warn',
          summary: status ? 'Activado' : 'Desactivado',
          detail: `${provider.provider_name} fue ${status ? 'activado' : 'desactivado'}`,
        });
        this.loadProviders(Math.floor(this.first / this.rows) + 1, this.searchTerm);
      },
      error: (error) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: error.message || 'No se pudo actualizar el estado',
        });
      },
    });
  }

  getSelectedStatusIcon(): string {
    const option = this.statusOptions.find((opt) => opt.value === this.selectedStatus);
    return option?.icon || 'pi pi-list';
  }

  getSelectedStatusLabel(): string {
    const option = this.statusOptions.find((opt) => opt.value === this.selectedStatus);
    return option?.label || 'Estado';
  }

  getCoesSelectedStatusIcon(): string {
    const option = this.coesStatusOptions.find((opt) => opt.value === this.selectedCoesStatus);
    return option?.icon || 'pi pi-list';
  }

  getCoesSelectedStatusLabel(): string {
    const option = this.coesStatusOptions.find((opt) => opt.value === this.selectedCoesStatus);
    return option?.label || 'Todos';
  }

  onPageChange(event: { page: number; rows: number }) {
    if (this.rows !== event.rows || this.first !== (event.page - 1) * event.rows) {
      this.rows = event.rows;
      this.first = (event.page - 1) * event.rows;
      this.loadProviders(event.page, this.searchTerm);
    }
  }

  private setupCountrySearch() {
    this.countrySearch$
      .pipe(debounceTime(400), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((q) => {
        this.providerService.getCountries(q).subscribe((res) => {
          this.countriesOptions = res.items.map((item) => ({
            label: item.name,
            value: String(item.country),
          }));
        });
      });
  }

  private loadRolesProvider(): void {
    this.loadingRoles = true;

    this.userService.getRoles().subscribe({
      next: (roles: RoleOption[]) => {
        const providerRoles = roles.filter((role) => this.isProviderRoleOption(role));
        this.roles = providerRoles;
        this.loadingRoles = false;
      },
      error: (error) => {
        console.error('Error cargando roles:', error);
        this.loadingRoles = false;
      },
    });
  }

  private isProviderRoleOption(role: RoleOption): boolean {
    const normalized = this.normalizeText(role?.label ?? role?.value ?? '');
    return normalized.includes('proveedor') || normalized.includes('provider');
  }

  private normalizeText(value: string): string {
    return Array.from((value ?? '').normalize('NFD'))
      .filter((ch) => {
        const cp = ch.codePointAt(0) ?? 0;
        return cp < 0x300 || cp > 0x036f;
      })
      .join('')
      .trim()
      .toLowerCase();
  }

  private setupProvinceSearch() {
    this.provinceSearch$
      .pipe(debounceTime(400), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((q) => {
        this.providerService.getProvinces(q).subscribe((res) => {
          this.provincesOptions = res.items.map((item) => ({
            label: item.name,
            value: String(item.city),
          }));
        });
      });
  }

  private setupCommuneSearch() {
    this.communeSearch$
      .pipe(debounceTime(400), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((q) => {
        this.providerService.getCommunes(q).subscribe((res) => {
          this.communesOptions = res.items.map((item) => ({
            label: item.name,
            value: String(item.comune),
          }));
        });
      });
  }

  private prepareFormData(raw: ProviderFormData, original?: Provider) {
    const isPeru = this.isPeru;

    // CAMPOS SIEMPRE PRESENTES
    const base = {
      provider_name: raw.provider_name,
      business_name: raw.business_name,
      ruc: raw.RUC,
      email: raw.email,
      phone: raw.phone,

      user: {
        password: raw.user_data?.password || undefined,
        name: raw.provider_name ?? original?.provider_name ?? '',
        role: raw.role ?? original?.user_data?.role ?? '',
      },

      netsuite_id: original?.netsuite_id ?? null,
      address_id: original?.address_id ?? null,
      coes: original?.coes ?? null,
      taxregime: original?.taxregime ?? null,
      is_active: original?.is_active ?? false,
      is_inactive: original?.is_inactive ?? false,
      is_deleted: original?.is_deleted ?? false,
      is_blocked: original?.is_blocked ?? false,
      ingested_at: original?.ingested_at ?? null,
      created_date: original?.created_date ?? null,
      last_modified_date: new Date().toISOString(),
    };

    // SI EL PAÍS NO ES PERÚ → ENVÍA SOLO LOS CAMPOS BASE
    if (!isPeru) {
      return {
        ...base,
        country: null,
        city: null,
        comune: null,
        address: undefined,
      };
    }

    // SI EL PAÍS ES PERÚ → ENVÍA TODO LO DEMÁS
    return {
      ...base,
      country: raw.country ?? original?.country ?? null,
      city: raw.city ?? original?.city ?? null,
      comune: raw.comune ?? original?.comune ?? null,
      address: raw.address ?? original?.address ?? null,
    };
  }

  isProviderUpdateBlocked(): boolean {
    return !this.hasRequiredProviderFields(this.currentEdit);
  }

  getProviderUpdateBlockedMessage(): string {
    const missingFields = this.getMissingRequiredProviderFields(this.currentEdit);
    if (missingFields.length === 0) {
      return '';
    }
    return `No se puede actualizar el proveedor porque faltan: ${missingFields.join(', ')}.`;
  }

  private getMissingRequiredProviderFields(
    provider: Partial<Provider> | null | undefined,
  ): string[] {
    if (!provider) {
      return ['País', 'Provincia', 'Comuna', 'Dirección', 'Correo', 'RUC', 'Razón social'];
    }

    const missing: string[] = [];

    if (!this.hasValue(provider.country)) {
      missing.push('País');
    }

    if (!this.hasValue(provider.city)) {
      missing.push('Provincia');
    }

    if (!this.hasValue(provider.comune)) {
      missing.push('Comuna');
    }

    if (!this.hasValue(provider.address)) {
      missing.push('Dirección');
    }

    if (!this.hasValue(provider.email)) {
      missing.push('Correo');
    }

    if (!this.hasValue(provider.ruc ?? provider.RUC)) {
      missing.push('RUC');
    }

    if (!this.hasValue(provider.business_name)) {
      missing.push('Razón social');
    }

    return missing;
  }

  private hasRequiredProviderFields(provider: Partial<Provider> | null | undefined): boolean {
    if (!provider) return false;
    return (
      this.hasValue(provider.country) &&
      this.hasValue(provider.city) &&
      this.hasValue(provider.comune) &&
      this.hasValue(provider.address) &&
      this.hasValue(provider.email) &&
      this.hasValue(provider.ruc ?? provider.RUC) &&
      this.hasValue(provider.business_name)
    );
  }

  private hasValue(value: string | null | undefined): boolean {
    return Boolean(value?.trim());
  }

  private buildSelectOptions(): void {
    this.translate
      .get([
        'providerModule.savings',
        'providerModule.checking',
        'currency.ARS',
        'currency.BOB',
        'currency.BRL',
        'currency.CAD',
        'currency.CLP',
        'currency.CNY',
        'currency.COP',
        'currency.EUR',
        'currency.MXN',
        'currency.PEN',
        'currency.USD',
        'currency.UYU',
        'currency.VES',
      ])
      .subscribe((translations: Record<string, string>) => {
        this.accountTypeOptions = [
          { label: translations['providerModule.savings'], value: 'savings' },
          { label: translations['providerModule.checking'], value: 'checking' },
        ];

        this.currencyOptions = [
          { label: translations['currency.ARS'], value: 'ARS' },
          { label: translations['currency.BOB'], value: 'BOB' },
          { label: translations['currency.BRL'], value: 'BRL' },
          { label: translations['currency.CAD'], value: 'CAD' },
          { label: translations['currency.CLP'], value: 'CLP' },
          { label: translations['currency.CNY'], value: 'CNY' },
          { label: translations['currency.COP'], value: 'COP' },
          { label: translations['currency.EUR'], value: 'EUR' },
          { label: translations['currency.MXN'], value: 'MXN' },
          { label: translations['currency.PEN'], value: 'PEN' },
          { label: translations['currency.USD'], value: 'USD' },
          { label: translations['currency.UYU'], value: 'UYU' },
          { label: translations['currency.VES'], value: 'VES' },
        ];
      });
  }

  private initForms() {
    this.createProviderForm = this.fb.group({
      provider_name: ['', [Validators.required]],
      business_name: ['', Validators.required],
      RUC: ['', [Validators.required, Validators.pattern(/^\d+$/)]],
      user_data: this.fb.group({
        password: ['', this.providerPasswordValidator],
      }),
      address: ['', Validators.required],
      city: ['', Validators.required],
      phone: ['', [Validators.required, Validators.pattern(/^\d+$/)]],
      email: ['', [Validators.required, Validators.email]],
      bank: ['', Validators.required],
      account_number: ['', [Validators.required, Validators.pattern(/^\d+$/)]],
      account_type: ['', Validators.required],
      currency: ['', Validators.required],
    });

    this.editProviderForm = this.fb.group({
      provider_name: [''],
      business_name: [''],
      RUC: [''],
      email: [''],
      phone: [''],
      address: [''],
      bank: [''],
      account_number: [''],
      account_type: [''],
      currency: [''],
      country: [''],
      city: [''],
      comune: [''],
      role: [''],
      user_data: this.fb.group({
        password: ['', [this.providerPasswordValidator]],
      }),
    });
  }

  private setupSearchDebounce() {
    this.search$
      .pipe(debounceTime(500), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((term) => this.loadProviders(1, term));
  }

  private setupStatusDebounce() {
    this.status$
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((status) => {
        this.selectedStatus = status;
        this.loadProviders(1, this.searchTerm);
      });
  }

  private setupCoesStatusDebounce() {
    this.coesStatus$
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((status) => {
        this.selectedCoesStatus = status;
        this.loadProviders(1, this.searchTerm);
      });
  }

  private loadProviders(page: number, search = '') {
    const currentPage = page ?? Math.floor(this.first / this.rows) + 1;

    this.providerService
      .getProviders(currentPage, this.rows, this.selectedStatus, search, this.selectedCoesStatus)
      .subscribe({
        next: (response) => {
          this.enforceInactiveStatusForInvalidProviders(response.data, currentPage, search);

          response.data = response.data.map((p: Provider) => ({
            ...p,
            showFullName: false,
            showFullAddress: false,
            showFullProviderName: false,
            showFullBusinessName: false,
          }));

          this.totalRecords = response.total ?? 0;
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'No se pudieron cargar los proveedores. Verifica la conexión.',
          });
        },
      });
  }

  private enforceInactiveStatusForInvalidProviders(
    providers: Provider[],
    currentPage: number,
    search: string,
  ): void {
    const providersToDeactivate = providers.filter(
      (provider) =>
        provider.is_active &&
        !this.hasRequiredProviderFields(provider) &&
        !this.autoDeactivatingProviderIds.has(provider.id),
    );

    if (providersToDeactivate.length === 0) {
      return;
    }

    providersToDeactivate.forEach((provider) => this.autoDeactivatingProviderIds.add(provider.id));

    const requests = providersToDeactivate.map((provider) =>
      this.providerService.updateProvider(provider.id, { is_active: false }).pipe(
        catchError((error) => {
          console.error(`No se pudo desactivar automáticamente el proveedor ${provider.id}`, error);
          return of(null);
        }),
      ),
    );

    forkJoin(requests).subscribe((results) => {
      providersToDeactivate.forEach((provider) =>
        this.autoDeactivatingProviderIds.delete(provider.id),
      );

      const successCount = results.filter((result) => result !== null).length;
      const failedCount = providersToDeactivate.length - successCount;

      if (successCount > 0) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Proveedores desactivados',
          detail: `Se desactivaron ${successCount} proveedor(es) activos con datos incompletos.`,
        });
        this.loadProviders(currentPage, search);
      }

      if (failedCount > 0) {
        this.messageService.add({
          severity: 'error',
          summary: 'Desactivación incompleta',
          detail: `No se pudieron desactivar ${failedCount} proveedor(es) con datos incompletos.`,
        });
      }
    });
  }

  private providerPasswordValidator(control: AbstractControl) {
    const value = control.value || '';

    // Si el campo está vacío, no marcamos error (sobre todo en edición)
    if (!value || value.trim().length === 0) {
      return null;
    }

    const errors: Record<string, boolean> = {};

    if (!/[A-Z]/.test(value)) {
      errors['uppercase'] = true;
    }
    if (!/[a-z]/.test(value)) {
      errors['lowercase'] = true;
    }
    if (value.length < 10) {
      errors['characters'] = true;
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(value)) {
      errors['special'] = true;
    }

    return Object.keys(errors).length ? errors : null;
  }
}
